import { ComponentRef } from "@angular/core";
import { filter, merge, Observable, Subject, take } from "rxjs";
import { FocusOrigin } from "@angular/cdk/a11y";
import { DialogRef } from "@angular/cdk/dialog";
import { DialogPosition, VdsDialogConfig } from "./dialog-config";
import { GlobalPositionStrategy } from "@angular/cdk/overlay";
import { VdsDialogContainer } from "./dialog-container/dialog-container";
import { ESCAPE, hasModifierKey } from "@angular/cdk/keycodes";

/** Possible states of the lifecycle of a dialog. */
export const enum VdsDialogState {
    OPEN,
    CLOSING,
    CLOSED
}

export class VdsDialogRef<T, R = any> {
    /** The instance of component opened into the dialog. */
    componentInstance!: T;

    /**
     * `ComponentRef` of the component opened into the dialog. Will be
     * null when the dialog is opened using a `TemplateRef`.
     */
    readonly componentRef: ComponentRef<T> | null = null;

    /** Whether the user is allowed to close the dialog. */
    disableClose: boolean | undefined;

    /** Unique ID for the dialog. */
    id: string;

    /** Subject for notifying the user that the dialog has finished opening. */
    private readonly _afterOpened = new Subject<void>();

    /** Subject for notifying the user that the dialog has started closing. */
    private readonly _beforeClosed = new Subject<R | undefined>();

    /** Result to be passed to afterClosed. */
    private result: R | undefined;

    /** Handle to the timeout that's running as a fallback in case the exit animation doesn't fire. */
    private closeFallbackTimeout: any | number;

    /** Current state of the dialog. */
    private state = VdsDialogState.OPEN;

    private closeInteractionType: FocusOrigin | undefined;

    constructor(
        private ref: DialogRef<R, T>,
        config: VdsDialogConfig,
        public containerInstance: VdsDialogContainer
    ) {
        this.disableClose = config.disableClose;
        this.id = ref.id;

        // Emit when opening animation completes
        containerInstance.animationStateChanged
            .pipe(
                filter((event) => event.state === "opened"),
                take(1)
            )
            .subscribe(() => {
                this._afterOpened.next();
                this._afterOpened.complete();
            });

        // Dispose overlay when closing animation is complete
        containerInstance.animationStateChanged
            .pipe(
                filter((event) => event.state === "closed"),
                take(1)
            )
            .subscribe(() => {
                clearTimeout(this.closeFallbackTimeout);
                this.finishDialogClose();
            });

        ref.overlayRef.detachments().subscribe(() => {
            this._beforeClosed.next(this.result);
            this._beforeClosed.complete();
            this.finishDialogClose();
        });

        merge(
            this.backdropClick(),
            this.keydownEvents().pipe(filter((event) => event.keyCode === ESCAPE && !this.disableClose && !hasModifierKey(event)))
        ).subscribe((event) => {
            if (!this.disableClose) {
                event.preventDefault();
                closeDialogVia(this, event.type === "keydown" ? "keyboard" : "mouse");
            }
        });
    }

    close(dialogResult?: R): void {
        this.result = dialogResult;

        this.containerInstance.animationStateChanged
            .pipe(
                filter((event) => event.state === "closing"),
                take(1)
            )
            .subscribe((event) => {
                this._beforeClosed.next(dialogResult);
                this._beforeClosed.complete();
                this.ref.overlayRef.detachBackdrop();

                // The logic that disposes of the overlay depends on the exit animation completing, however
                // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
                // timeout which will clean everything up if the animation hasn't fired within the specified
                // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
                // vast majority of cases the timeout will have been cleared before it has the chance to fire.
                this.closeFallbackTimeout = setTimeout(() => this.finishDialogClose(), event.totalTime + 100);
            });

        this.state = VdsDialogState.CLOSING;
        this.containerInstance._startExitAnimation();
    }

    afterOpened(): Observable<void> {
        return this._afterOpened;
    }

    afterClosed(): Observable<R | undefined> {
        return this.ref.closed;
    }

    beforeClosed(): Observable<R | undefined> {
        return this._beforeClosed;
    }

    backdropClick(): Observable<MouseEvent> {
        return this.ref.backdropClick;
    }

    keydownEvents(): Observable<KeyboardEvent> {
        return this.ref.keydownEvents;
    }

    updatePosition(position?: DialogPosition): this {
        const strategy = this.ref.config.positionStrategy as GlobalPositionStrategy;

        if (position && (position.left || position.right)) {
            position.left ? strategy.left(position.left) : strategy.right(position.right);
        } else {
            strategy.centerHorizontally();
        }

        if (position && (position.top || position.bottom)) {
            position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
        } else {
            strategy.centerVertically();
        }

        this.ref.updatePosition();

        return this;
    }

    /**
     * Updates the dialog's width and height.
     * @param width New width of the dialog.
     * @param height New height of the dialog.
     */
    updateSize(width = "", height = ""): this {
        this.ref.updateSize(width, height);
        return this;
    }

    /** Add a CSS class or an array of classes to the overlay pane. */
    addPanelClass(classes: string | string[]): this {
        this.ref.addPanelClass(classes);
        return this;
    }

    /** Remove a CSS class or an array of classes from the overlay pane. */
    removePanelClass(classes: string | string[]): this {
        this.ref.removePanelClass(classes);
        return this;
    }

    /** Gets the current state of the dialog's lifecycle. */
    getState(): VdsDialogState {
        return this.state;
    }

    /**
     * Finishes the dialog close by updating the state of the dialog
     * and disposing the overlay.
     */
    private finishDialogClose() {
        this.state = VdsDialogState.CLOSED;
        this.ref.close(this.result, { focusOrigin: this.closeInteractionType });
        this.componentInstance = null!;
    }
}

export function closeDialogVia<R>(ref: VdsDialogRef<R>, interactionType: FocusOrigin, result?: R) {
    (ref as unknown as { _closeInteractionType: FocusOrigin })._closeInteractionType = interactionType;
    return ref.close(result);
}
